/**
 * Choosing an audit's data scope — which of a client's processed rows the
 * audit covers. One editor, used by "New audit" step 2 and by the Data tab's
 * "Change data scope", so a scope can be changed exactly the way it was made.
 *
 * Two columns: the file group and the files (chips) on the left; the live
 * count and the conditions on the right. The count answers every change; while a new count is coming
 * the old one is never shown as if it were current, and the caller is told it
 * is not ready yet. Nothing ticked and nothing typed means every processed row.
 */

import { useModule } from '../modules/useModule';
import { useEffect, useMemo, useState } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	TextField,
	Typography,
} from '@mui/material';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import {
	DatePicker,
	Field,
	MetricCard,
	MultiSelect,
	Note,
	Select,
} from '../../shared/ui';
import { useAuth } from '../../provider/auth';
import { count } from '../../utils/format';
import { isIncluded } from '../../utils/files';
import {
	useCanonicalRowPreviewQuery,
	useClientFilesQuery,
} from '../../queries/clients';
import {
	useFileGroupQuery,
	useFileGroupsQuery,
} from '../../queries/fileGroups';
import { ErrorState } from '../shared/states';
import type { CanonicalRowFilter } from '../../schemas';

type TextKey =
	| 'periodStart'
	| 'periodEnd'
	| 'customerId'
	| 'materialId'
	| 'plantCode'
	| 'documentType'
	| 'classification';

const TEXT_KEYS: TextKey[] = [
	'periodStart',
	'periodEnd',
	'customerId',
	'materialId',
	'plantCode',
	'documentType',
	'classification',
];

/** A stored filter (unknown shape) as the editor's value. A single legacy
 *  `fileId` becomes a one-file selection. */
export function toScope(raw: unknown): CanonicalRowFilter {
	if (!raw || typeof raw !== 'object') return {};
	const source = raw as Record<string, unknown>;
	const result: CanonicalRowFilter = {};
	for (const key of TEXT_KEYS) {
		const value = source[key];
		if (typeof value === 'string' && value) result[key] = value;
	}
	const ids = new Set<string>();
	if (Array.isArray(source.fileIds))
		for (const id of source.fileIds) ids.add(String(id));
	if (source.fileId) ids.add(String(source.fileId));
	if (ids.size > 0) result.fileIds = [...ids];
	return result;
}

/** The scope as the preview endpoint's query string. */
export function scopeQuery(filter: CanonicalRowFilter): string {
	const params = new URLSearchParams();
	for (const key of TEXT_KEYS) {
		const value = filter[key];
		if (value) params.set(key, value);
	}
	if (filter.fileIds?.length) params.set('fileIds', filter.fileIds.join(','));
	return params.toString();
}

/** The live count for a scope. No file chosen means no rows: nothing is asked
 *  of the server and the count is 0 (`preview.data` is then not this scope's —
 *  an empty filter shares its cache key with the all-files total — so it is
 *  never read). `ready` only once the count for THIS scope has arrived and
 *  matches at least one row. */
export function useScopeCount(
	clientId: string | null,
	filter: CanonicalRowFilter,
	enabled = true,
) {
	const { module } = useModule();
	const chosen = (filter.fileIds?.length ?? 0) > 0;
	const preview = useCanonicalRowPreviewQuery(
		clientId,
		module,
		scopeQuery(filter),
		enabled && chosen,
	);
	const counting = chosen && preview.isFetching;
	return {
		preview,
		chosen,
		counting,
		rowCount: !chosen
			? 0
			: counting
				? null
				: (preview.data?.rowCount ?? null),
		ready:
			chosen &&
			Boolean(preview.data) &&
			!counting &&
			(preview.data?.rowCount ?? 0) > 0,
	};
}

/** Preview keys are classification values; UNCLASSIFIED is the null bucket. */
function classificationKey(key: string): string {
	return key === 'UNCLASSIFIED' ? 'UNKNOWN' : key;
}

export function DataScopeEditor({
	clientId,
	clientName,
	value,
	onChange,
	enabled = true,
}: {
	clientId: string;
	clientName: string;
	value: CanonicalRowFilter;
	onChange: (next: CanonicalRowFilter) => void;
	enabled?: boolean;
}) {
	const { vocabulary } = useAuth();
	const { module } = useModule();
	/* An H2R audit's scope is its files and nothing else: there are no row
     filters, no classification, and the count is employees. */
	const isH2R = module === 'H2R';
	const classLabel = (key: string) =>
		vocabulary?.classification?.[classificationKey(key)]?.label ?? key;

	const library = useClientFilesQuery(clientId, module, enabled);
	const usable = useMemo(
		() => (library.data ?? []).filter(isIncluded),
		[library.data],
	);

	const [groupId, setGroupId] = useState('');
	const groups = useFileGroupsQuery(enabled ? clientId : null, module);
	const group = useFileGroupQuery(groupId || null, Boolean(groupId));

	const selected = useMemo(
		() => new Set(value.fileIds ?? []),
		[value.fileIds],
	);
	const setFiles = (ids: string[]) =>
		onChange({ ...value, fileIds: ids.length ? ids : undefined });
	const setText = (key: TextKey, next: string) =>
		onChange({ ...value, [key]: next || undefined });

	const visibleFiles = useMemo(() => {
		if (!groupId || !group.data) return usable;
		const inGroup = new Set(group.data.files.map((file) => file.pkFileId));
		return usable.filter((file) => inGroup.has(file.pkFileId));
	}, [groupId, group.data, usable]);

	/* Choosing a group selects its processed files. */
	useEffect(() => {
		if (!groupId || !group.data) return;
		const processed = new Set(usable.map((file) => file.pkFileId));
		setFiles(
			group.data.files
				.map((file) => file.pkFileId)
				.filter((id) => processed.has(id)),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groupId, group.data]);

	const base = useCanonicalRowPreviewQuery(clientId, module, '', enabled);
	const classificationOptions = useMemo(
		() =>
			Object.keys(base.data?.byClassification ?? {})
				.sort()
				.map((key) => ({ value: key, label: classLabel(key) })),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[base.data, vocabulary],
	);

	const scope = useScopeCount(clientId, value, enabled);
	const noData = base.data?.rowCount === 0;

	/* The live answer: how many rows this scope covers, by classification —
     the same card as every other figure in the app. */
	const countPanel = noData ? (
		<Note tone="warn" row title="No processed files yet">
			{clientName} has no processed files. The data-entry team prepares
			files in Client files.
		</Note>
	) : !scope.chosen ? (
		/* No file chosen, no rows: the audit covers only the files picked. */
		<Box aria-live="polite">
			<MetricCard
				label={isH2R ? 'Employees in scope' : 'Rows in scope'}
				icon={<StorageOutlinedIcon />}
				tone="accent"
				value={isH2R ? '0 employees' : count(0)}
				meta="No files chosen yet"
			/>
		</Box>
	) : scope.preview.error ? (
		<ErrorState
			error={scope.preview.error}
			onRetry={scope.preview.refetch}
		/>
	) : !scope.counting && scope.preview.data?.rowCount === 0 ? (
		<Note tone="warn" row title="No rows match">
			{isH2R
				? 'Choose more files.'
				: 'Widen the conditions or choose more files.'}
		</Note>
	) : (
		<Box aria-live="polite">
			<MetricCard
				label={isH2R ? 'Employees in scope' : 'Rows in scope'}
				icon={<StorageOutlinedIcon />}
				tone="accent"
				value={
					scope.counting || !scope.preview.data ? (
						<CircularProgress
							size={16}
							aria-label="Counting rows"
						/>
					) : isH2R ? (
						`${count(scope.preview.data.rowCount)} employees`
					) : (
						count(scope.preview.data.rowCount)
					)
				}
				meta={
					isH2R
						? scope.counting || !scope.preview.data
							? 'Counting…'
							: undefined
						: scope.preview.data && !scope.counting
							? Object.entries(
									scope.preview.data.byClassification,
								)
									.sort(([, x], [, y]) => y - x)
									.map(
										([key, rows]) =>
											`${classLabel(key)} ${count(rows)}`,
									)
									.join(' · ')
							: 'Counting…'
				}
			/>
		</Box>
	);

	return (
		<Box
			/* Two columns that stack when the editor itself is narrow (a dialog,
         a small screen). */
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
				gap: 'var(--s5)',
				alignItems: 'stretch',
			}}
		>
			{/* Left: which files. */}
			<Box
				component="section"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 'var(--s3)',
					minWidth: 0,
					'& .field': { mb: 0 },
				}}
			>
				<Field
					label="File group"
					hint="Optional. Selects the processed files in a saved group."
				>
					{(id) => (
						<Select
							id={id}
							value={groupId}
							placeholder="No group"
							emptyLabel="No file groups for this client yet"
							options={[
								{ value: '', label: 'No group' },
								...(groups.data ?? []).map((item) => ({
									value: item.pkFileGroupId,
									label: item.name,
									hint: `${item.fileCount} file${item.fileCount === 1 ? '' : 's'}`,
								})),
							]}
							onChange={setGroupId}
						/>
					)}
				</Field>
				<ErrorState error={library.error} onRetry={library.refetch} />
				<Field
					label="Files"
					required
					hint="Pick the files this audit covers. Select all takes every processed file."
				>
					{(id) => (
						<MultiSelect
							id={id}
							values={[...selected]}
							onChange={setFiles}
							placeholder={
								groupId && visibleFiles.length === 0
									? 'This group has no processed files'
									: `Choose from ${count(visibleFiles.length)} processed files`
							}
							emptyLabel="No processed files yet"
							options={visibleFiles.map((file) => ({
								value: file.pkFileId,
								label: file.originalName,
								hint:
									file.rowCount !== null
										? `${count(file.rowCount)} rows`
										: undefined,
							}))}
						/>
					)}
				</Field>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 'var(--s2)',
					}}
				>
					<Typography
						component="span"
						sx={{
							fontSize: 'var(--fs-small)',
							color: 'var(--ink-3)',
						}}
					>
						{selected.size > 0
							? `${count(selected.size)} of ${count(visibleFiles.length)} files chosen`
							: 'No files chosen yet.'}
					</Typography>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: '2px',
							'& .MuiButton-root': {
								minWidth: 0,
								px: 'var(--s1)',
								py: 0,
							},
						}}
					>
						<Button
							size="small"
							variant="text"
							disabled={
								visibleFiles.length === 0 ||
								selected.size === visibleFiles.length
							}
							onClick={() =>
								setFiles(
									visibleFiles.map((file) => file.pkFileId),
								)
							}
						>
							Select all
						</Button>
						<Typography
							component="span"
							aria-hidden="true"
							sx={{ color: 'var(--ink-3)' }}
						>
							·
						</Typography>
						<Button
							size="small"
							variant="text"
							disabled={selected.size === 0}
							onClick={() => setFiles([])}
						>
							Clear
						</Button>
					</Box>
				</Box>
			</Box>

			{/* Right: the count, then the conditions that narrow the rows. */}
			<Box
				component="section"
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: 'var(--s3)',
					minWidth: 0,
					'& .field': { mb: 0 },
				}}
			>
				{countPanel}
				{!isH2R && (
					<>
						<Typography
							component="h3"
							sx={{
								m: 0,
								fontSize: 'var(--fs-section)',
								fontWeight: 600,
								color: 'var(--ink)',
							}}
						>
							Conditions
						</Typography>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns:
									'repeat(2, minmax(0, 1fr))',
								gap: 'var(--s3)',
								'& .field': { mb: 0 },
							}}
						>
							<Field label="Invoice date from" hint="Optional">
								{(id) => (
									<DatePicker
										id={id}
										value={value.periodStart ?? ''}
										onChange={(next) =>
											setText('periodStart', next)
										}
									/>
								)}
							</Field>
							<Field label="Invoice date to" hint="Optional">
								{(id) => (
									<DatePicker
										id={id}
										value={value.periodEnd ?? ''}
										onChange={(next) =>
											setText('periodEnd', next)
										}
									/>
								)}
							</Field>
							{(
								[
									['customerId', 'Customer code'],
									['materialId', 'Material code'],
									['plantCode', 'Plant'],
									['documentType', 'Document type'],
								] as const
							).map(([key, label]) => (
								<Field
									key={key}
									label={label}
									hint="Optional. As it appears in the register."
								>
									{(id) => (
										<TextField
											id={id}
											size="small"
											fullWidth
											value={value[key] ?? ''}
											onChange={(event) =>
												setText(key, event.target.value)
											}
										/>
									)}
								</Field>
							))}
						</Box>
						<Field label="Classification" hint="Optional">
							{(id) => (
								<Select
									id={id}
									value={value.classification ?? ''}
									placeholder="Any classification"
									emptyLabel="No classified rows for this client yet"
									options={[
										{
											value: '',
											label: 'Any classification',
										},
										...classificationOptions,
									]}
									onChange={(next) =>
										setText('classification', next)
									}
								/>
							)}
						</Field>
					</>
				)}
			</Box>
		</Box>
	);
}
